import { ComponentType, LazyExoticComponent } from 'react';
import { lazyWithRetry } from '@/utils/lazyWithRetry';
import type { FeatureFlagKey } from '@/context/RemoteConfigContext';

/**
 * Route table — the single source of truth mapping URLs to pages.
 *
 * Convention:
 * - `src/pages/` holds one component per routed page (full-page views).
 *   Feature code they compose stays in `src/components/<feature>/` — most
 *   feature dirs are shared by several pages, so they are not co-located here.
 * - Overlay/modal components (sheets, modals, slide-in panels) are NOT pages;
 *   they live in `src/components/` and are rendered by pages or the app shell.
 * - `layout: 'main'` pages render inside ResponsiveLayout (nav + chrome);
 *   `layout: 'full-screen'` pages render bare (exercise player, OAuth return,
 *   onboarding). A few full-screen pages are entry points into larger feature
 *   flows and stay co-located with their feature dir (see below).
 *
 * ViewRouter maps over this table; adding a page = one file in pages/ plus
 * one entry here.
 */

// Lazy-loaded pages with automatic retry on chunk load failure.
// Using .then() to handle named exports since all pages use named exports.
// `/` goes through HomeRoute, which consults the first_session_v2 slot and
// renders HomeView for control or Home v2 for the variant.
const HomeView = lazyWithRetry(() =>
  import('./HomeRoute').then(m => ({ default: m.HomeRoute }))
);
const ReflectView = lazyWithRetry(() =>
  import('./ReflectView').then(m => ({ default: m.ReflectView }))
);
const GrowView = lazyWithRetry(() =>
  import('./GrowView').then(m => ({ default: m.GrowView }))
);
const PauseView = lazyWithRetry(() =>
  import('./PauseView').then(m => ({ default: m.PauseView }))
);
const ConnectView = lazyWithRetry(() =>
  import('./ConnectView').then(m => ({ default: m.ConnectView }))
);
const ProfileView = lazyWithRetry(() =>
  import('./ProfileView').then(m => ({ default: m.ProfileView }))
);
const GoalsView = lazyWithRetry(() =>
  import('./GoalsView').then(m => ({ default: m.GoalsView }))
);
const JournalView = lazyWithRetry(() =>
  import('./JournalView').then(m => ({ default: m.JournalView }))
);
const SubscriptionsView = lazyWithRetry(() =>
  import('./SubscriptionsView').then(m => ({ default: m.SubscriptionsView }))
);
const StoryView = lazyWithRetry(() =>
  import('./StoryView').then(m => ({ default: m.StoryView }))
);
const CartCompanionView = lazyWithRetry(() =>
  import('./CartCompanionView').then(m => ({ default: m.CartCompanionView }))
);
const DebtManagerView = lazyWithRetry(() =>
  import('./DebtManagerView').then(m => ({ default: m.DebtManagerView }))
);
const NoSpendWeekendView = lazyWithRetry(() =>
  import('./NoSpendWeekendView').then(m => ({ default: m.NoSpendWeekendView }))
);
const WhereItGoesView = lazyWithRetry(() =>
  import('./WhereItGoesView').then(m => ({ default: m.WhereItGoesView }))
);
const ContactView = lazyWithRetry(() =>
  import('./ContactView').then(m => ({ default: m.ContactView }))
);
const LegalView = lazyWithRetry(() =>
  import('./LegalView').then(m => ({ default: m.LegalView }))
);
const UpgradeView = lazyWithRetry(() =>
  import('./UpgradeView').then(m => ({ default: m.UpgradeView }))
);
const NotFoundView = lazyWithRetry(() =>
  import('./NotFoundView').then(m => ({ default: m.NotFoundView }))
);

// Full-screen pages that are entry points into larger feature flows. They stay
// co-located with their feature dir rather than in pages/ — the flow's code is
// only used through them.
const ExerciseModuleView = lazyWithRetry(() =>
  import('@/components/exercises/ExerciseModuleView').then(m => ({ default: m.ExerciseModuleView }))
);
const PlaidOAuthReturn = lazyWithRetry(() =>
  import('@/components/connect/PlaidOAuthReturn').then(m => ({ default: m.PlaidOAuthReturn }))
);
const OnboardingFlow = lazyWithRetry(() =>
  import('@/components/onboarding/OnboardingFlow').then(m => ({ default: m.OnboardingFlow }))
);

export interface PageRoute {
  path: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Component: LazyExoticComponent<ComponentType<any>>;
  /** 'main' renders inside ResponsiveLayout; 'full-screen' renders bare. */
  layout: 'main' | 'full-screen';
  /** Route redirects to home unless this Remote Config flag is enabled. */
  featureFlag?: FeatureFlagKey;
  /** Page receives the personalization context as a `state` prop. */
  withContextState?: boolean;
}

export const pageRoutes: PageRoute[] = [
  // Main navigation pages
  { path: '/', Component: HomeView, layout: 'main', withContextState: true },
  { path: '/reflect', Component: ReflectView, layout: 'main', withContextState: true },
  { path: '/grow', Component: GrowView, layout: 'main' },
  { path: '/pause', Component: PauseView, layout: 'main' },
  { path: '/connect', Component: ConnectView, layout: 'main' },

  // Menu / secondary pages
  { path: '/profile', Component: ProfileView, layout: 'main' },
  { path: '/goals', Component: GoalsView, layout: 'main', featureFlag: 'goals' },
  { path: '/journal', Component: JournalView, layout: 'main' },
  { path: '/subscriptions', Component: SubscriptionsView, layout: 'main', featureFlag: 'subscriptions' },
  { path: '/story', Component: StoryView, layout: 'main' },
  { path: '/cart-companion', Component: CartCompanionView, layout: 'main', featureFlag: 'cartCompanion' },
  { path: '/debt', Component: DebtManagerView, layout: 'main', featureFlag: 'debtManager' },
  { path: '/no-spend-weekend', Component: NoSpendWeekendView, layout: 'main', featureFlag: 'quietDays' },
  { path: '/flow', Component: WhereItGoesView, layout: 'main', featureFlag: 'whereItGoes' },
  { path: '/contact', Component: ContactView, layout: 'main' },
  { path: '/legal', Component: LegalView, layout: 'main' },

  // Email campaign deep link: paywall for free users, home for paid
  { path: '/upgrade', Component: UpgradeView, layout: 'main' },

  // Full-screen pages (no layout wrapper)
  { path: '/exercise/:id', Component: ExerciseModuleView, layout: 'full-screen' },
  { path: '/oauth-return', Component: PlaidOAuthReturn, layout: 'full-screen' },
  { path: '/welcome', Component: OnboardingFlow, layout: 'full-screen' },

  // Catch-all
  { path: '*', Component: NotFoundView, layout: 'main' },
];

/**
 * Deep-link allowlist, DERIVED from the route table above — one source of
 * truth. AuthGuard uses it to decide whether a post-login intended path is a
 * real app route (navigate there) or junk (fall back to home). Deriving it
 * here means a new page added to `pageRoutes` is automatically deep-linkable;
 * there is no second hand-maintained list to drift out of sync (the bug this
 * replaced silently rejected deep links to `/pause` and `/debt`).
 *
 * Parameterized routes (`/exercise/:id`) contribute a path PREFIX
 * (`/exercise/`) so any concrete id matches. Static routes match exactly.
 *
 * Two deliberate exclusions, both explicit (not by omission):
 * - the catch-all (`*`) — it's the 404 fallback, never a real destination.
 * - feature-flagged routes are INCLUDED, not excluded: AuthGuard only answers
 *   "is this a shape the router knows". The flag-off redirect to home is
 *   ViewRouter's job (`FeatureFlaggedRoute`), unchanged — so deep-linking to a
 *   flag-off route keeps its current behavior (routes there, then ViewRouter
 *   sends it home if the flag is off).
 */
const parameterized = (path: string) => path.includes(':');

export const deepLinkableExactRoutes: ReadonlySet<string> = new Set(
  pageRoutes.filter(r => r.path !== '*' && !parameterized(r.path)).map(r => r.path)
);

export const deepLinkableRoutePrefixes: readonly string[] = pageRoutes
  .filter(r => parameterized(r.path))
  .map(r => r.path.slice(0, r.path.indexOf(':')));

/** True if `pathname` corresponds to a route the app knows how to render. */
export const isDeepLinkableRoute = (pathname: string): boolean =>
  deepLinkableExactRoutes.has(pathname) ||
  deepLinkableRoutePrefixes.some(prefix => pathname.startsWith(prefix));
